import React, { useCallback } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { ObjectDetailsSpacer } from '@atlassian/jira-servicedesk-insight-attribute-views/src/common/ui/styled.tsx';
import { GroupsReadView } from '@atlassian/jira-servicedesk-insight-attribute-views/src/ui/group/index.tsx';
import { GroupsPicker } from '@atlassian/jira-servicedesk-insight-groups-picker/src/ui/index.tsx';
import {
	useGroupValue,
	getGroupValues,
} from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/group/index.tsx';
import { useEditableAttribute } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/use-editable-attribute/index.tsx';
import type { GroupAttributeType } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute-type.tsx';
import type { Group } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/insight-roles.tsx';
import { EditViewSpacers } from '../../common/ui/edit-view-spacers/styled.tsx';
import { InlineEditableAttribute } from '../../common/ui/editable-attribute/index.tsx';

type Props = {
	isEditable: boolean;
	attributeType: GroupAttributeType;
};

const toFieldValue = (values: Array<Group>) => values.map(({ name }) => name);

const EditableGroupAttribute = (props: Props) => {
	const { attributeType, isEditable } = props;

	const [{ attribute }] = useEditableAttribute(attributeType);
	const [value, setValue] = useGroupValue(attribute);
	const storedValue = toFieldValue(getGroupValues(attribute));
	const isMultiPicker = attributeType.maximumCardinality !== 1;

	const setGroupValue = useCallback(
		(groups: Group[]) => {
			setValue(groups.filter(({ name }) => name != null));
		},
		[setValue],
	);
	const resetValue = useCallback(() => setValue(getGroupValues(attribute)), [attribute, setValue]);

	return (
		<InlineEditableAttribute
			testId="servicedesk-insight-inline-editable-attributes.ui.group-attribute"
			value={toFieldValue(value)}
			onChange={setGroupValue}
			storedValue={storedValue}
			attributeType={attributeType}
			isEditable={isEditable}
			attributeValuesInfo={{ type: 'group', attributeValues: value }}
			scalingEnabled
			renderReadView={() => (
				<ObjectDetailsSpacer>
					<GroupsReadView groups={value} />
				</ObjectDetailsSpacer>
			)}
			renderEditView={({ errorMessage, hasValidationError, ...fieldProps }) => (
				<EditViewSpacers.GroupPicker>
					<GroupsPicker
						{...fieldProps}
						autoFocus
						defaultMenuIsOpen
						value={value}
						isMulti={isMultiPicker}
						validationState={hasValidationError ? 'error' : 'default'}
						classNamePrefix="insight-object-attribute-group-select"
					/>
				</EditViewSpacers.GroupPicker>
			)}
			onResetValue={resetValue}
		/>
	);
};

export const GroupEditableAttribute = (props: Props) => (
	<JSErrorBoundary
		id="InsightEditableGroupAttribute"
		packageName="jiraServicedeskInsightEditableGroupAttribute"
		teamName="falcons"
		fallback="unmount"
	>
		<EditableGroupAttribute {...props} />
	</JSErrorBoundary>
);
